// PURPOSE: Displays P&L statistics, inputs for profit target/stop loss, and trade summary
// EXTRACTED FROM: Current index.js P&L Card content

import React from 'react';
import { Button, Descriptions, Divider, InputNumber, Space, Statistic } from 'antd';
import PropTypes from 'prop-types';

import { FORMAT } from '../../../../../utilities/functions';

import './styles.scss';

export default function ProfitLossTab({
    trade,
    profitTargetValue,
    stopLossValue,
    initialTarget,
    tradeRisk,
    plannedR,
    realizedR,
    onProfitTargetChange,
    onStopLossChange,
    onCalculateExcursions,
    isCalculatingExcursions,
    isViewAsActive
}) {
    const showExcursionsButton =
        (!trade.mfe ||
            trade.mfe === 'N/A' ||
            !trade.theoreticalMfe ||
            trade.theoreticalMfe === 'N/A' ||
            !trade.mae ||
            trade.mae === 'N/A') &&
        stopLossValue;

    return (
        <div className="profit-loss-tab">
            {/* Main P&L Statistic */}
            <div className="pnl-header">
                <Statistic
                    title="Profit & Loss"
                    value={trade.netPnL}
                    precision={2}
                    valueStyle={
                        FORMAT.CURRENCY_TO_NUMBER(trade.netPnL) > 0
                            ? { color: '#29a373', fontSize: '28px', fontWeight: 600 }
                            : { color: '#E57373', fontSize: '28px', fontWeight: 600 }
                    }
                />
            </div>

            {trade.status !== 'open' && <Divider style={{ margin: '16px 0' }} />}

            {/* Risk Management Inputs */}
            <div className="risk-inputs">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <InputNumber
                        addonBefore="Profit Target"
                        value={profitTargetValue}
                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        onChange={onProfitTargetChange}
                        disabled={isViewAsActive}
                        style={{ width: '100%' }}
                    />
                    <InputNumber
                        addonBefore="Stop Loss"
                        value={stopLossValue}
                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        onChange={onStopLossChange}
                        disabled={isViewAsActive}
                        style={{ width: '100%' }}
                    />
                    {showExcursionsButton && (
                        <Button
                            type="primary"
                            onClick={onCalculateExcursions}
                            loading={isCalculatingExcursions}
                            disabled={isViewAsActive}
                            style={{ width: '100%' }}
                        >
                            Calculate Excursions (MFE/MAE)
                        </Button>
                    )}
                </Space>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Trade Summary */}
            <Descriptions
                title="Summary"
                bordered
                column={1}
                size="small"
                className="trade-summary"
            >
                <Descriptions.Item label="Symbol">{trade.symbol}</Descriptions.Item>
                <Descriptions.Item label="Side">{trade.side}</Descriptions.Item>
                <Descriptions.Item label="Open Time">{trade.openTime}</Descriptions.Item>
                <Descriptions.Item label="Close Time">{trade.closeTime}</Descriptions.Item>
                <Descriptions.Item label="Open Price">{trade.openPrice}</Descriptions.Item>
                <Descriptions.Item label="Close Price">{trade.closePrice}</Descriptions.Item>
                <Descriptions.Item label="MFE">{trade.mfe || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Theoretical MFE">
                    {trade.theoreticalMfe || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="MAE">{trade.mae || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Total Volume">{trade.totalVolume}</Descriptions.Item>
                <Descriptions.Item label="Number of Executions">
                    {trade.executionsNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Planned Reward">{initialTarget}</Descriptions.Item>
                <Descriptions.Item label="Trade Risk">{tradeRisk}</Descriptions.Item>
                <Descriptions.Item label="Planned R-Multiple">{plannedR}</Descriptions.Item>
                <Descriptions.Item label="Realized R-Multiple">{realizedR}</Descriptions.Item>
            </Descriptions>
        </div>
    );
}

ProfitLossTab.propTypes = {
    trade: PropTypes.object.isRequired,
    profitTargetValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stopLossValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialTarget: PropTypes.string,
    tradeRisk: PropTypes.string,
    plannedR: PropTypes.string,
    realizedR: PropTypes.string,
    onProfitTargetChange: PropTypes.func.isRequired,
    onStopLossChange: PropTypes.func.isRequired,
    onCalculateExcursions: PropTypes.func.isRequired,
    isCalculatingExcursions: PropTypes.bool,
    isViewAsActive: PropTypes.bool
};
